import { useEffect, useState } from 'react'
import {
  Modal,
  TextInput,
  NumberInput,
  MultiSelect,
  Autocomplete,
  Switch,
  Button,
  Group,
  Stack,
  TagsInput,
  Alert,
  Text,
  List,
  Badge,
  Divider,
  ActionIcon,
  Table,
} from '@mantine/core'
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react'
import {
  Title,
  CreateTitleData,
  UpdateTitleData,
  TitleDuplicateWarning,
  TitleClientRef,
} from '../../api/titlesApi'
import { Client } from '../../api/clientsApi'
import { Application } from '../../api/applicationsApi'

interface TitleFormProps {
  opened: boolean
  onClose: () => void
  onSubmit: (data: CreateTitleData | UpdateTitleData) => Promise<boolean>
  title?: Title | null
  duplicateWarning?: TitleDuplicateWarning | null
  onClearDuplicateWarning?: () => void
  clients: Client[]
  applications: Application[]
}

interface FormErrors {
  name?: string
  releaseYear?: string
  externalIds?: string
}

interface ExternalIdRow {
  key: string
  value: string
}

function externalIdsToRows(externalIds: Record<string, string>): ExternalIdRow[] {
  return Object.entries(externalIds).map(([key, value]) => ({ key, value }))
}

function rowsToExternalIds(rows: ExternalIdRow[]): Record<string, string> {
  const result: Record<string, string> = {}
  for (const row of rows) {
    const k = row.key.trim()
    const v = row.value.trim()
    if (k && v) result[k] = v
  }
  return result
}

export function TitleForm({
  opened,
  onClose,
  onSubmit,
  title,
  duplicateWarning,
  onClearDuplicateWarning,
  clients,
  applications,
}: TitleFormProps) {
  const isEdit = !!title

  const [name, setName] = useState('')
  const [aliases, setAliases] = useState<string[]>([])
  const [releaseYear, setReleaseYear] = useState<number | string>('')
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([])
  const [externalIdRows, setExternalIdRows] = useState<ExternalIdRow[]>([])
  const [isActive, setIsActive] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const clientOptions = clients.map((c) => ({ value: c._id, label: c.name }))

  // Autocomplete options for the external ID "System" key field.
  // Each entry shows the appId (stored as key) and app name as hint.
  const appIdOptions = applications.map((a) => ({
    value: a.appId,
    label: `${a.appId} — ${a.name}`,
  }))

  useEffect(() => {
    if (opened) {
      if (title) {
        setName(title.name)
        setAliases(title.aliases)
        setReleaseYear(title.releaseYear ?? '')
        setSelectedClientIds(title.clients.map((c) => c.clientId))
        setExternalIdRows(externalIdsToRows(title.externalIds ?? {}))
        setIsActive(title.isActive)
      } else {
        setName('')
        setAliases([])
        setReleaseYear('')
        setSelectedClientIds([])
        setExternalIdRows([])
        setIsActive(true)
      }
      setErrors({})
      onClearDuplicateWarning?.()
    }
  }, [opened, title])

  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    } else if (name.length > 300) {
      newErrors.name = 'Name must be 300 characters or less'
    }

    if (releaseYear !== '' && releaseYear !== null) {
      const year = Number(releaseYear)
      if (!Number.isInteger(year) || year < 1888 || year > new Date().getFullYear() + 5) {
        newErrors.releaseYear = 'Enter a valid 4-digit year'
      }
    }

    // Check for duplicate keys in externalIds
    const keys = externalIdRows.map((r) => r.key.trim()).filter(Boolean)
    if (new Set(keys).size !== keys.length) {
      newErrors.externalIds = 'External ID keys must be unique'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const buildClients = (): TitleClientRef[] =>
    selectedClientIds.map((id) => ({ clientId: id }))

  const buildExternalIds = (): Record<string, string> =>
    rowsToExternalIds(externalIdRows)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)

    let data: CreateTitleData | UpdateTitleData
    const year = releaseYear !== '' ? Number(releaseYear) : undefined

    if (isEdit) {
      data = {
        name: name.trim(),
        aliases,
        releaseYear: year ?? null,
        clients: buildClients(),
        externalIds: buildExternalIds(),
        isActive,
      } as UpdateTitleData
    } else {
      data = {
        name: name.trim(),
        aliases,
        releaseYear: year,
        clients: buildClients(),
        externalIds: buildExternalIds(),
      } as CreateTitleData
    }

    const success = await onSubmit(data)
    setIsSubmitting(false)
    if (success) onClose()
  }

  const handleConfirmCreate = async () => {
    setIsSubmitting(true)
    const year = releaseYear !== '' ? Number(releaseYear) : undefined
    const data: CreateTitleData = {
      name: name.trim(),
      aliases,
      releaseYear: year,
      clients: buildClients(),
      externalIds: buildExternalIds(),
      confirmCreate: true,
    }
    const success = await onSubmit(data)
    setIsSubmitting(false)
    if (success) onClose()
  }

  const addExternalIdRow = () =>
    setExternalIdRows((rows) => [...rows, { key: '', value: '' }])

  const updateExternalIdRow = (index: number, field: 'key' | 'value', val: string) =>
    setExternalIdRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: val } : row))
    )

  const removeExternalIdRow = (index: number) =>
    setExternalIdRows((rows) => rows.filter((_, i) => i !== index))

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit Title' : 'New Title'}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="The Wolf of Wall Street"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              onClearDuplicateWarning?.()
            }}
            error={errors.name}
            maxLength={300}
          />

          <TagsInput
            label="Aliases"
            placeholder="Type an alias and press Enter"
            value={aliases}
            onChange={(v) => {
              setAliases(v)
              onClearDuplicateWarning?.()
            }}
            description="Alternate titles, working titles, or regional variants"
            clearable
          />

          <NumberInput
            label="Release Year"
            placeholder="2013"
            value={releaseYear}
            onChange={setReleaseYear}
            min={1888}
            max={new Date().getFullYear() + 5}
            hideControls
            error={errors.releaseYear}
          />

          <MultiSelect
            label="Clients"
            placeholder="Select clients..."
            data={clientOptions}
            value={selectedClientIds}
            onChange={setSelectedClientIds}
            searchable
            clearable
            description="Distributors or studios associated with this title"
          />

          <Divider label="External IDs" labelPosition="left" />

          {externalIdRows.length > 0 && (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>System</Table.Th>
                  <Table.Th>ID</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {externalIdRows.map((row, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Autocomplete
                        placeholder="e.g. flightplan"
                        data={appIdOptions}
                        value={row.key}
                        onChange={(val) => updateExternalIdRow(i, 'key', val)}
                        size="xs"
                        comboboxProps={{ withinPortal: true }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        placeholder="ID value"
                        value={row.value}
                        onChange={(e) => updateExternalIdRow(i, 'value', e.target.value)}
                        size="xs"
                      />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => removeExternalIdRow(i)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {errors.externalIds && (
            <Text size="xs" c="red">
              {errors.externalIds}
            </Text>
          )}

          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={addExternalIdRow}
            w="fit-content"
          >
            Add External ID
          </Button>

          {isEdit && (
            <Switch
              label="Active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          )}

          {duplicateWarning && (
            <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Potential Duplicates Found">
              <Stack gap="xs">
                <Text size="sm">The following existing titles look similar:</Text>
                <List size="sm" spacing="xs">
                  {duplicateWarning.matches.map((match) => (
                    <List.Item key={match._id}>
                      <Group gap="xs">
                        <Text fw={500}>{match.name}</Text>
                        {match.releaseYear && (
                          <Text size="xs" c="dimmed">({match.releaseYear})</Text>
                        )}
                        {match.aliases.length > 0 && (
                          <Text size="xs" c="dimmed">{match.aliases.join(', ')}</Text>
                        )}
                        <Badge size="xs" variant="light">
                          {Math.round((1 - match.score) * 100)}% match
                        </Badge>
                      </Group>
                    </List.Item>
                  ))}
                </List>
                <Group mt="xs">
                  <Button
                    size="xs"
                    variant="filled"
                    color="yellow"
                    onClick={handleConfirmCreate}
                    loading={isSubmitting}
                  >
                    Create Anyway
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => onClearDuplicateWarning?.()}
                  >
                    Go Back
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {!duplicateWarning && (
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                {isEdit ? 'Save Changes' : 'Create Title'}
              </Button>
            </Group>
          )}
        </Stack>
      </form>
    </Modal>
  )
}
